import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CheckCircle, ArrowRight, Sparkles, Ruler, Target, Camera, Facebook } from "lucide-react";
import { useState } from "react";
import { GalleryLightbox } from "@/components/GalleryLightbox";

const MechanicalPolishing = () => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const polishingGallery = [
    {
      src: "/lovable-uploads/92dab0d6-229c-4bf8-be51-7f4060c5ebff.png",
      alt: "Perforated stainless steel plate with precision polished holes",
      caption: "Precision perforated plate with mechanically polished surface"
    },
    {
      src: "/lovable-uploads/0ccb06c2-40c1-4d11-8d78-9086b6bb3dcd.png", 
      alt: "Large polished dish-end with mirror finish",
      caption: "Large diameter dish-end with mirror polished surface"
    },
    {
      src: "/lovable-uploads/91a18a67-1010-4109-96b6-b8a9f9363fee.png",
      alt: "Dish-end component being polished on specialized equipment",
      caption: "Specialized polishing equipment for curved components"
    },
    {
      src: "/lovable-uploads/a14eae2b-6ef9-42c4-bf7e-549e7b7ab3be.png",
      alt: "Large polished dish-end in workshop environment",
      caption: "Large capacity dish-end polishing in our facility"
    },
    {
      src: "/lovable-uploads/dbbc02e9-7bcf-487b-92e5-b82b5afbc3eb.png",
      alt: "Highly reflective polished stainless steel surface showing mirror finish",
      caption: "Mirror finish quality achieved through mechanical polishing"
    },
    {
      src: "/lovable-uploads/78af7b21-a909-4045-96e6-e027d5a1de9c.png",
      alt: "Interior surface of polished tank showing smooth finish",
      caption: "Interior tank surface with uniform polished finish"
    },
    {
      src: "/lovable-uploads/2e9e203d-f2fd-4087-99ff-bc5d67f9d693.png",
      alt: "Polished dish-end showing circular polishing patterns",
      caption: "Dish-end component showing professional polishing patterns"
    }
  ];

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };
  const specifications = [
    {
      title: "Plate Polishing",
      size: "1524mm (W) × 9144mm (L)",
      description: "Semi-automatic wide sand belt polishing machine",
      icon: Ruler
    },
    {
      title: "Dish-End Polishing", 
      size: "Up to 4000mm diameter",
      description: "Specialized equipment for curved surfaces",
      icon: Target
    },
    {
      title: "Cylindrical Polishing",
      size: "Max height: 1524mm, Max diameter: 4000mm",
      description: "Complete shell polishing capabilities",
      icon: Sparkles
    }
  ];

  const finishOptions = [
    { grade: "2B Finish", ra: "0.5 μm", application: "Standard food grade applications" },
    { grade: "BA Finish", ra: "0.3 μm", application: "High-end food & beverage" },
    { grade: "Mirror Finish", ra: "0.1 μm", application: "Pharmaceutical & biotech" },
    { grade: "Custom Finish", ra: "As specified", application: "Special requirements" }
  ];

  const process = [
    {
      step: "01",
      title: "Surface Preparation",
      description: "Cleaning and assessment of surface condition"
    },
    {
      step: "02", 
      title: "Mechanical Polishing",
      description: "Progressive grit polishing to required finish"
    },
    {
      step: "03",
      title: "Quality Testing",
      description: "MITUTOYO surface roughness verification"
    },
    {
      step: "04",
      title: "Final Inspection",
      description: "Visual and dimensional quality checks"
    }
  ];

  const advantages = [
    "Largest polishing capacity in Southeast Asia",
    "MITUTOYO calibrated surface testing equipment",
    "Consistent mirror finish quality",
    "Reduced lead times with in-house capability",
    "Cost-effective compared to outsourcing",
    "Quality control throughout the process",
    "Various finish grades available",
    "Suitable for food, pharma, and chemical industries"
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Mechanical Polishing Services | Mirror Finish Ra 0.1μm - WLE</title>
        <meta name="description" content="Advanced mechanical polishing with mirror finish quality Ra 0.1μm. Plate polishing up to 1524×9144mm, dish-end polishing up to 4000mm diameter. MITUTOYO certified." />
        <link rel="canonical" href="https://wletec.com/services/mechanical-polishing" />
        <meta property="og:title" content="Mechanical Polishing Services - WLE Technology" />
        <meta property="og:description" content="State-of-the-art mechanical polishing for mirror-quality surface finishes." />
        <meta property="og:url" content="https://wletec.com/services/mechanical-polishing" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="mechanical polishing, mirror finish, surface roughness, dish-end polishing, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="mb-4">Mechanical Polishing</Badge>
            <h1 className="text-5xl font-bold text-foreground mb-6">
              Advanced <span className="text-wle-blue">Mechanical Polishing</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              State-of-the-art mechanical polishing facilities delivering mirror-quality surface finishes 
              with verified roughness measurements for critical applications.
            </p>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Equipment Specifications</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {specifications.map((spec, index) => (
              <Card key={index} className="text-center hover:shadow-blue transition-all duration-300">
                <CardHeader>
                  <div className="mx-auto mb-4 p-4 rounded-full bg-wle-blue/10">
                    <spec.icon className="h-8 w-8 text-wle-blue" />
                  </div>
                  <CardTitle className="text-xl mb-2">{spec.title}</CardTitle>
                  <CardDescription className="text-lg font-semibold text-wle-blue">{spec.size}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{spec.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Finish Options */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Available Finish Grades</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {finishOptions.map((finish, index) => (
              <Card key={index} className="hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-lg">{finish.grade}</CardTitle>
                  <CardDescription className="text-sm">Ra {finish.ra}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{finish.application}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Mechanical Polishing Showcase</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See our precision mechanical polishing work across various components and finishes, 
              from perforated plates to large dish-ends with mirror-quality results.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {polishingGallery.map((image, index) => (
              <div
                key={index}
                className="relative aspect-square bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <Camera className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Polishing Process</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2 text-lg">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Choose Our Polishing Services?</h2>
              <p className="text-muted-foreground mb-8">
                Our advanced mechanical polishing capabilities ensure consistent, high-quality surface finishes 
                that meet the strictest industry standards for pharmaceutical, food, and chemical applications.
              </p>
              <ul className="space-y-4">
                {advantages.map((advantage, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-wle-blue mt-0.5 flex-shrink-0" />
                    <span>{advantage}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="p-8 bg-gradient-section">
                <div className="text-center">
                  <Sparkles className="h-16 w-16 text-wle-blue mx-auto mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Mirror Finish Quality</h3>
                  <div className="space-y-4 text-left">
                    <div className="flex justify-between">
                      <span>Best Surface Roughness:</span>
                      <span className="font-semibold">Ra 0.1 μm</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Testing Equipment:</span>
                      <span className="font-semibold">MITUTOYO Calibrated</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Max Plate Size:</span>
                      <span className="font-semibold">1524 × 9144 mm</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Max Dish-End:</span>
                      <span className="font-semibold">4000 mm diameter</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">Need Professional Polishing?</h3>
              <p className="text-lg mb-8 opacity-90">
                Get a quote for your polishing requirements. We handle everything from small components 
                to large tank shells with guaranteed quality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" variant="secondary">
                  <Link to="/contact">
                    Request Polishing Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                  <a 
                    href="https://www.facebook.com/profile.php?id=100054675420087" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    <Facebook className="h-4 w-4" />
                    WLE Electro Polish
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
      
      <GalleryLightbox
        items={polishingGallery}
        startIndex={selectedImageIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
      />
    </div>
  );
};

export default MechanicalPolishing;